<template>
    <section class="relative mt-5 space-y-5 md:mt-10">
        <template v-if="data">
            <section
                v-for="group in data"
                :key="group.title"
                :id="group.category_slug"
                :aria-labelledby="`${group.category_slug}-title`"
                class="relative scroll-m-25 overflow-hidden rounded-12 bg-[#FFFFFF0D] p-5"
                :class="categoryCardClass">
                <h2
                    :id="`${group.category_slug}-title`"
                    class="relative z-10 mb-5 text-32/100 font-semibold text-ash-200">
                    {{ group.title }}
                </h2>

                <AccordionRoot
                    type="single"
                    collapsible
                    v-model="activeAccordion">
                    <AccordionItem
                        v-for="groupQuestion in group.data"
                        :key="groupQuestion.id"
                        :id="groupQuestion.slug"
                        :value="groupQuestion.slug"
                        class="right-0 mt-1 scroll-m-25 overflow-hidden rounded-12 p-5 transition-all duration-300 first:mt-0"
                        :class="questionCardClass">
                        <AccordionHeader class="relative z-20 flex">
                            <AccordionTrigger
                                class="group flex flex-1 items-center justify-between text-24/133 font-semibold text-white outline-none transition-all duration-300">
                                <h3 class="text-left">
                                    {{ groupQuestion.question }}
                                </h3>
                                <div class="ml-4">
                                    <PlusIcon
                                        name="close"
                                        class="size-8 text-ash-200 transition-all duration-300 group-data-[state=open]:rotate-45" />
                                </div>
                            </AccordionTrigger>
                        </AccordionHeader>
                        <AccordionContent
                            class="overflow-hidden data-[state=closed]:animate-[slideUp_450ms] data-[state=open]:animate-[slideDown_450ms]">
                            <div
                                v-html="groupQuestion.answer"
                                class="faq-answer tinyeditor relative z-10 max-w-[800px] pt-3 text-16/148 font-medium text-ash-200"></div>
                        </AccordionContent>
                    </AccordionItem>
                </AccordionRoot>
            </section>
        </template>
        <template v-else-if="questions">
            <AccordionRoot
                type="single"
                collapsible
                v-model="activeAccordion">
                <AccordionItem
                    v-for="groupQuestion in questions"
                    :key="groupQuestion.id"
                    :id="groupQuestion.slug"
                    :value="groupQuestion.slug"
                    class="right-0 mt-1 scroll-m-25 overflow-hidden rounded-12 p-5 transition-all duration-300 first:mt-0"
                    :class="questionCardClass">
                    <AccordionHeader class="relative z-20 flex">
                        <AccordionTrigger
                            class="group flex flex-1 items-center justify-between text-24/133 font-semibold text-white outline-none transition-all duration-300">
                            <h3 class="text-left">
                                {{ groupQuestion.question }}
                            </h3>
                            <div class="ml-4">
                                <PlusIcon
                                    name="close"
                                    class="size-8 text-ash-200 transition-all duration-300 group-data-[state=open]:rotate-45" />
                            </div>
                        </AccordionTrigger>
                    </AccordionHeader>
                    <AccordionContent
                        class="overflow-hidden data-[state=closed]:animate-[slideUp_450ms] data-[state=open]:animate-[slideDown_450ms]">
                        <div
                            v-html="groupQuestion.answer"
                            class="faq-answer tinyeditor relative z-10 max-w-[800px] pt-3 text-16/148 font-medium text-ash-200"></div>
                    </AccordionContent>
                </AccordionItem>
            </AccordionRoot>
        </template>
    </section>
</template>

<script setup lang="ts">
import {
    AccordionContent,
    AccordionHeader,
    AccordionItem,
    AccordionRoot,
    AccordionTrigger,
} from 'reka-ui'

import PlusIcon from '~/components/pages/directions/faq-section/svg/PlusIcon.vue'
import type {
    FaqCategory,
    FaqCategoryType,
} from '~/components/pages/faq/faq-category/faq-categories.types'

interface Props {
    data?: FaqCategoryType[]
    questions?: FaqCategory[]
}

const props = defineProps<Props>()

const activeAccordion = defineModel<string | undefined>()
const isAccordionInitialized = ref(Boolean(activeAccordion.value))

const categoryCardClass =
    'isolate before:pointer-events-none before:absolute before:left-0 before:top-0 before:z-0 before:h-[440px] before:w-[573px] before:translate-x-[-22%] before:translate-y-[-62%] before:rounded-full before:bg-[radial-gradient(ellipse_at_center,rgba(116,63,218,0.55)_0,rgba(116,63,218,0.42)_6px,rgba(116,63,218,0.22)_34%,rgba(116,63,218,0.08)_58%,rgba(116,63,218,0)_78%)]'

const questionCardClass = 'relative isolate bg-[#6f43b463]'

const initialQuestion = computed(
    () => props.data?.[0]?.data?.[0]?.slug || props.questions?.[0]?.slug
)

initializeAccordion()

watch(initialQuestion, initializeAccordion)

function initializeAccordion() {
    if (isAccordionInitialized.value) return

    if (activeAccordion.value) {
        isAccordionInitialized.value = true
        return
    }

    if (!initialQuestion.value) return

    activeAccordion.value = initialQuestion.value
    isAccordionInitialized.value = true
}
</script>
